import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, Utils } from 'app/main/components';
import { useModel, useSettings, useUserData } from 'app/main/components/hooks';
import { DateTimeField, SimpleTextField } from 'app/main/components/fields';
import { uploadRecipients } from './DemsManagerActions';

interface DemsSettingsDialogProps {
	model: any;
	onSuccess: VoidFunction;
}

export const showDemsSettingsDialog = (props: DemsSettingsDialogProps) =>
	openDialog({ children: <DemsSettingsDialog {...props} />, onClose: () => {} });

export default function DemsSettingsDialog(props: DemsSettingsDialogProps) {
	const dispatch = useDispatch();
	const { settings, sysSettings, t } = useSettings('dems');
	const userData = useUserData();

	const { model, sendChange, save } = useModel({
		baseModel: props.model,
		baseModifiedModel: {
			dems_sender_name: sysSettings[`mailing_${props.model.dems_connector}_sender_name`],
			dems_sender_mail: sysSettings[`mailing_${props.model.dems_connector}_sender_mail`],
			dems_default_language:
				props.model.dems_connector === 'mailchimp' && sysSettings.mailing_mailchimp_sender_default_language
		},
		isEdit: true,
		settings
	});

	function submitSave() {
		dispatch(Utils.alertProgressMsg(t));
		save({
			onSuccess: () => {
				dispatch(closeDialog());
				uploadRecipients(
					{
						dispatch,
						t,
						demsConnector: props.model.dems_connector,
						demsUid: props.model.dems_uid,
						userUid: userData.user_uid
					},
					props.onSuccess
				);
			}
		});
	}

	return (
		<>
			<DialogTitle>{t('DEMS_SETTINGS_DIALOG_TITLE')}</DialogTitle>
			<DialogContent>
				<Form id="demsSettingsDialog" onSubmit={submitSave}>
					<Grid container spacing={2}>
						{props.model.dems_connector === 'mailchimp' && (
							<>
								<Grid size={12}>
									<SimpleTextField
										name="dems_subject"
										label={t('DETAILS_DEMS_FIELD_SUBJECT')}
										value={model.dems_subject}
										isEdit
										sendChange={sendChange}
										required
										validationError={t('ERROR_TEXT_REQUIRED')}
									/>
								</Grid>
								<Grid size={12}>
									<SimpleTextField
										name="dems_preview_text"
										label={t('DETAILS_DEMS_FIELD_PREVIEWTEXT')}
										value={model.dems_preview_text}
										isEdit
										sendChange={sendChange}
									/>
								</Grid>
							</>
						)}
						<Grid size={12}>
							<SimpleTextField
								name="dems_sender_name"
								label={t('DETAILS_DEMS_FIELD_SENDER_NAME')}
								value={model.dems_sender_name}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						<Grid size={{ md: 6, xs: 12 }}>
							<SimpleTextField
								name="dems_sender_mail"
								label={t('DETAILS_DEMS_FIELD_SENDER_MAIL')}
								value={model.dems_sender_mail}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{props.model.dems_connector === 'mailchimp' && (
							<Grid size={{ md: 6, xs: 12 }}>
								<SimpleTextField
									name="dems_default_language"
									label={t('DETAILS_DEMS_FIELD_DEFAULT_LANGUAGE')}
									value={model.dems_default_language}
									isEdit
									sendChange={sendChange}
								/>
							</Grid>
						)}
						<Grid size={{ md: 6, xs: 12 }}>
							<DateTimeField
								name="dems_date"
								label={t('DETAILS_DEMS_FIELD_DATE')}
								value={model.dems_date}
								isEdit
								sendChange={sendChange}
								validations={{ minDateTime: Utils.today().toDate() }}
								validationErrors={{
									minDateTime: (
										<FormattedMessage
											id="minDateTimeError"
											defaultMessage={t('ERROR_MIN_DATE')}
											values={{ value: Utils.today().format('DD/MM/yyyy HH:mm') }}
										/>
									)
								}}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="demsSettingsDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
